import { timestampFromDate } from '@bufbuild/protobuf/wkt'
import { utc } from '@date-fns/utc'
import { EditOutlined } from '@mui/icons-material'
import { Pagination, Box, Grow, PaginationItem } from '@mui/material'
import { createFileRoute, stripSearchParams } from '@tanstack/react-router'
import { addDays, formatISO, startOfDay } from 'date-fns'
import { User } from 'oidc-client-ts'
import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from 'react-oidc-context'
import { z } from 'zod/v4-mini'
import { FabLink } from '@/components/FabLink'
import { SearchField } from '@/components/SearchField'
import { NoTrackings } from '@/features/trackings/components/NoTrackings'
import { PaginationItemLink } from '@/features/trackings/components/PaginationItemLink'
import { TrackingsList } from '@/features/trackings/components/TrackingList'
import { TrackingsFilters } from '@/features/trackings/components/TrackingsFilters'
import { TrackingsListPendingComponent } from '@/features/trackings/components/TrackingsListPendingComponent'
import { TrackingsProjectFilterChip } from '@/features/trackings/components/TrackingsProjectFilterChip'
import { TrackingsTimespanFilterChip } from '@/features/trackings/components/TrackingsTimespanFilterChip'
import { createTrackingSearching } from '@/features/trackings/helper/createTrackingsSearching'
import { DeletedItemSelection } from '@/generated/Enumerations/DeletedItemSelection_pb'
import { ProjectSortBy } from '@/generated/Enumerations/ProjectSortBy_pb'
import { TrackingSortBy } from '@/generated/Enumerations/TrackingSortBy_pb'
import { TrackingFiltersDto } from '@/generated/Messages/Filters_pb'
import { assertNotNullish } from '@/helper/assertNotNullish'
import { dateTimeOffsetDtoToDate } from '@/helper/dateTimeOffsetDto'
import { debounce } from '@/helper/debounce'
import { parseId, stringifyId } from '@/helper/id'
import { useInterval } from '@/hooks/useInterval'
import { projectsQueryOptions, useProjects } from '@/queries/useProjects'
import { trackingsQueryOptions, useTrackings } from '@/queries/useTrackings'
import {
    searchParamsPaginationDefaultValues,
    searchParamsPaginationSchema,
} from '@/schemas/searchParams/searchParamsPaginationSchema'
import {
    searchParamsProjectIds,
    searchParamsProjectIdsDefaultValue,
} from '@/schemas/searchParams/searchParamsProjectIds'
import {
    searchParamsSearchTextDefaultValues,
    searchParamsSearchTextSchema,
} from '@/schemas/searchParams/searchParamsSearchTextSchema'
import { searchParamsToFromSchema } from '@/schemas/searchParams/searchParamsToFromSchema'
import { OmitDeepMessageFields } from '@/types/OmitDeepMessageFields'

const seleniumPrefix = 'trackings-list'

const queryFilters = (
    searchText: string,
    pageSize: number,
    page: number,
    user?: User | null,
    from?: string | null,
    to?: string | null,
    projects?: string[]
): Partial<OmitDeepMessageFields<TrackingFiltersDto>> => ({
    sorting: [{ sortBy: TrackingSortBy.STARTED_AT, descending: true }],
    deletedItemSelection: DeletedItemSelection.NORMAL_ONLY,
    searching: createTrackingSearching(searchText),
    pagination: {
        takeItemCount: pageSize,
        skipItemCount: page * pageSize,
    },
    userIds: user?.profile.sub ? [parseId(user.profile.sub)] : undefined,
    dateTimeFrom:
        from ? timestampFromDate(startOfDay(from, { in: utc })) : undefined,
    dateTimeTo:
        to ?
            timestampFromDate(startOfDay(addDays(to, 1), { in: utc }))
        :   undefined,
    projectIds: projects ? projects.map(parseId) : undefined,
})

const RouteComponent: React.FC = () => {
    useInterval(60 * 1000)

    const { searchText, page, pageSize, from, to, projectIds } =
        Route.useSearch()
    const parsedFrom = from ? new Date(from) : undefined
    const parsedTo = to ? new Date(to) : undefined

    const navigate = Route.useNavigate()
    const { t } = useTranslation()

    const [localSearchText, setLocalSearchText] = React.useState(searchText)

    const { user } = useAuth()

    const {
        data: { trackings, pagination: { totalItemCount } = {} },
    } = useTrackings(
        queryFilters(searchText, pageSize, page, user, from, to, projectIds)
    )
    const {
        data: { projects },
    } = useProjects({
        userIds: user?.profile.sub ? [parseId(user.profile.sub)] : undefined,
        deletedItemSelection: DeletedItemSelection.NORMAL_ONLY,
        sorting: [{ sortBy: ProjectSortBy.NAME }],
    })

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const debouncedNavigate = React.useCallback(
        debounce(async (searchText: string) => {
            await navigate({
                search: prev => ({ ...prev, searchText, page: undefined }),
            })
        }),
        []
    )

    const largestPossiblePage = Math.ceil((totalItemCount ?? 0) / pageSize)

    return (
        <>
            <title>{t('common.trackings')}</title>

            <SearchField
                fullWidth={true}
                value={localSearchText}
                onChange={({ target: { value } }) => {
                    setLocalSearchText(value)

                    // Change search params only after the timeout has finished.
                    debouncedNavigate(value)
                }}
                onClickReset={async () => {
                    setLocalSearchText('')

                    await navigate({
                        search: prev => ({
                            ...prev,
                            searchText: '',
                            page: undefined,
                        }),
                    })
                }}
                data-selenium={`${seleniumPrefix}-search-field`}
            />

            <Box
                sx={{
                    marginTop: 2,
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 1,
                }}
            >
                <TrackingsFilters
                    // Fixes state issues, because this way the state gets reset when from, to or projectIds changes.
                    key={`${from}_${to}_${JSON.stringify(projectIds)}`}
                    variant={'list'}
                    from={parsedFrom}
                    to={parsedTo}
                    projects={projectIds}
                    availableProjects={projects}
                    handleSubmit={async ({ from, to, projects }) => {
                        await navigate({
                            to: '.',
                            search: state => ({
                                ...state,
                                page: 0,
                                from:
                                    from ?
                                        formatISO(from, {
                                            representation: 'date',
                                        })
                                    :   undefined,
                                to:
                                    to ?
                                        formatISO(to, {
                                            representation: 'date',
                                        })
                                    :   undefined,
                                projectIds: projects,
                            }),
                        })
                    }}
                />

                {(from || to) && (
                    <TrackingsTimespanFilterChip
                        from={parsedFrom}
                        to={parsedTo}
                        onDelete={() =>
                            navigate({
                                to: '.',
                                search: prev => ({
                                    ...prev,
                                    page: 0,
                                    from: undefined,
                                    to: undefined,
                                }),
                            })
                        }
                    />
                )}

                {projectIds.flatMap(pId => {
                    const project = projects.find(
                        p => stringifyId(p.id) === pId
                    )

                    return project ?
                            [
                                <TrackingsProjectFilterChip
                                    key={pId}
                                    projectName={project.name}
                                    onDelete={() =>
                                        navigate({
                                            to: '.',
                                            search: prev => ({
                                                ...prev,
                                                page: 0,
                                                projectIds:
                                                    prev.projectIds.filter(
                                                        ppId => ppId !== pId
                                                    ),
                                            }),
                                        })
                                    }
                                />,
                            ]
                        :   []
                })}
            </Box>

            {trackings.length === 0 ?
                <NoTrackings />
            :   <TrackingsList
                    trackings={trackings}
                    projects={projects}
                    onClickTracking={({ id, startedAt }) =>
                        async () => {
                            assertNotNullish(id)
                            assertNotNullish(startedAt)

                            await navigate({
                                to: '.',
                                search: prev => ({
                                    ...prev,
                                    showEditTrackings: true,
                                    day: formatISO(
                                        dateTimeOffsetDtoToDate(startedAt),
                                        { representation: 'date' }
                                    ),
                                    trackingId: stringifyId(id),
                                }),
                            })
                        }}
                />
            }

            <Grow in={true}>
                <FabLink
                    variant={'extended'}
                    sx={theme => ({
                        position: 'absolute',
                        bottom:
                            user?.profile.role === 'Administrator' ?
                                theme.spacing(2)
                            :   { xs: theme.spacing(9), sm: theme.spacing(2) },
                        right: theme.spacing(2),
                    })}
                    color={'primary'}
                    to={'.'}
                    search={prev => ({ ...prev, showEditTrackings: true })}
                    data-selenium={`${seleniumPrefix}-edit`}
                >
                    <EditOutlined /> {t('common.edit')}
                </FabLink>
            </Grow>

            {largestPossiblePage > 1 && (
                <Pagination
                    variant={'outlined'}
                    style={{ width: '100%' }}
                    count={largestPossiblePage}
                    page={page + 1}
                    hideNextButton={true}
                    hidePrevButton={true}
                    renderItem={item =>
                        (
                            item.type === 'end-ellipsis' ||
                            item.type === 'start-ellipsis'
                        ) ?
                            <PaginationItem {...item} />
                        :   <PaginationItemLink
                                {...item}
                                to={'/trackings/list'}
                                search={prev => ({
                                    ...prev,
                                    page: (item.page ?? 1) - 1,
                                })}
                            />
                    }
                />
            )}
        </>
    )
}

const PendingComponent: React.FC = () => {
    const { searchText, from, to, projectIds } = Route.useSearch()

    return (
        <TrackingsListPendingComponent
            searchText={searchText}
            from={from ? new Date(from) : undefined}
            to={to ? new Date(to) : undefined}
            projectIds={projectIds}
        />
    )
}

export const Route = createFileRoute(
    '/__base-layout/__edit-trackings/trackings/list'
)({
    component: RouteComponent,
    validateSearch: z.object({
        ...searchParamsSearchTextSchema.shape,
        ...searchParamsPaginationSchema.shape,
        ...searchParamsToFromSchema.shape,
        ...searchParamsProjectIds.shape,
    }),

    search: {
        middlewares: [
            stripSearchParams({
                ...searchParamsSearchTextDefaultValues,
                ...searchParamsPaginationDefaultValues,
                ...searchParamsProjectIdsDefaultValue,
            }),
        ],
    },

    pendingComponent: PendingComponent,

    beforeLoad: async ({
        context: { queryClient, grpcClients, user },
        search: { searchText, page, pageSize, from, to, projectIds },
    }) => {
        await Promise.all([
            queryClient.ensureQueryData(
                trackingsQueryOptions(
                    grpcClients,
                    queryFilters(
                        searchText,
                        pageSize,
                        page,
                        user,
                        from,
                        to,
                        projectIds
                    )
                )
            ),
            queryClient.ensureQueryData(
                projectsQueryOptions(grpcClients, {
                    userIds:
                        user?.profile.sub ?
                            [parseId(user.profile.sub)]
                        :   undefined,
                    deletedItemSelection: DeletedItemSelection.NORMAL_ONLY,
                    sorting: [{ sortBy: ProjectSortBy.NAME }],
                })
            ),
        ])
    },
})
